import * as ko from 'knockout';
import * as moment from 'moment';
import { HistoryViewerStore } from './HistoryViewerStore';
import { Paginator } from 'Core/Components/Paginator/Paginator';
import { EVENTS as PAGINATOR_EVENTS } from 'Core/Components/Paginator/Constants';
import { BlockUI } from 'Core/Common/BlockUi';
import { Notifier } from 'Core/Common/Notifier';
import { LABELS, NOTIFICATIONS } from 'Core/Components/Translation/Locales';
import { DatetimepickerExtention } from 'Core/KnockoutExtentions/DatetimepickerExtention';

DatetimepickerExtention.Init();
import {
	IHistoryViewerRequest,
	IHistoryViewerOperationModel,
	IIdNamePair
} from './Models/HistoryViewerModels';

import Template from './HistoryViewerPage.html';

ko.templates['Pages/HistoryViewerPage/Templates/HistoryViewerPage'] = Template;

// CD_LK_OPERATIONTYPES ids — stable and locale-independent (unlike the translated name)
const OPERATION_TYPE = { Create: 1, Update: 2, Delete: 3 };

class HistoryViewerItemViewModel {
	OperationId: string;
	FormattedDate: string;
	TableName: string;
	TableId: number;
	RecordId: number;
	OperationName: string;
	OperationColor: string;
	FieldCount: number;
	ChangedBy: string;
	Trace: string;
	TraceTransaction: string;
	IpAddress: string;
	TraceUser: string;
	GeoLocation: KnockoutObservable<string>;
	IsCreate: boolean;
	IsDelete: boolean;
	IsExpanded: KnockoutObservable<boolean>;
	IsLoadingDetails: KnockoutObservable<boolean>;
	FieldChanges: KnockoutObservableArray<any>;
	SortedFieldChanges: KnockoutComputed<any[]>;

	constructor(data: IHistoryViewerOperationModel) {
		this.OperationId = data.OperationId;
		// CHANGEDATE is stored in UTC (history trigger uses GETUTCDATE); parse as UTC, show local.
		this.FormattedDate = data.ChangeDate ? moment.utc(data.ChangeDate).toDate().toLocaleString() : '';
		this.TableName = data.TableName;
		this.TableId = data.TableId;
		this.RecordId = data.RecordId;
		this.OperationName = data.OperationName;
		this.FieldCount = data.FieldCount;
		this.ChangedBy = data.ChangedBy;
		this.Trace = data.Trace;
		this.GeoLocation = ko.observable('');

		// Parse JSON trace: {"tx":"...","ip":"...","user":"..."}
		let traceObj: any = null;
		try { traceObj = data.Trace ? JSON.parse(data.Trace) : null; } catch { }

		if (traceObj) {
			this.TraceTransaction = traceObj.tx || '';
			this.IpAddress = traceObj.ip || '';
			this.TraceUser = traceObj.user || '';
		} else {
			// Fallback for old format "[IP] POST: /Api/..."
			const ipMatch = (data.Trace || '').match(/^\[(\d+\.\d+\.\d+\.\d+)\]/);
			this.IpAddress = ipMatch ? ipMatch[1] : '';
			this.TraceTransaction = data.Trace || '';
			this.TraceUser = '';
		}
		this.IsExpanded = ko.observable(false);
		this.IsLoadingDetails = ko.observable(false);
		this.FieldChanges = ko.observableArray([]);

		this.IsCreate = data.OperationTypeId === OPERATION_TYPE.Create;
		this.IsDelete = data.OperationTypeId === OPERATION_TYPE.Delete;
		this.OperationColor = this.IsCreate ? '#36c6d3'
			: this.IsDelete ? '#e7505a'
			: '#F1C40F';

		this.SortedFieldChanges = ko.computed(() => this.FieldChanges().slice().sort((a, b) => a.Sort - b.Sort));
	}
}

export class HistoryViewerPage {
	private _isReady: KnockoutObservable<boolean>;
	private _items: KnockoutObservableArray<HistoryViewerItemViewModel>;
	private _isLoading: KnockoutObservable<boolean>;
	private _paginator: Paginator;
	private _paginationInfo: KnockoutObservable<string>;

	// Filters
	private _dateFrom: KnockoutObservable<any>;
	private _dateTo: KnockoutObservable<any>;
	private _selectedTableId: KnockoutObservable<number | undefined>;
	private _selectedOperationType: KnockoutObservable<number | undefined>;
	private _recordId: KnockoutObservable<number | undefined>;
	private _changedBy: KnockoutObservable<string>;
	private _valueSearch: KnockoutObservable<string>;

	// Filter options
	private _tables: KnockoutObservableArray<IIdNamePair>;
	private _operations: KnockoutObservableArray<IIdNamePair>;

	// Sort
	private _sortColumn: KnockoutObservable<string>;
	private _sortDirection: KnockoutObservable<string>;

	// Active request (for cancellation / supersession)
	private _containerId: string;
	private _activeRequest: AbortController | null = null;

	// Exposed for translation bindings in the template (e.g. text: LABELS.CANCEL)
	private LABELS = LABELS;

	constructor() {
		this._isReady = ko.observable(false);
		this._items = ko.observableArray([]);
		this._isLoading = ko.observable(false);
		this._paginationInfo = ko.observable('');

		this._dateFrom = ko.observable(new Date());
		this._dateTo = ko.observable(new Date());
		this._selectedTableId = ko.observable(undefined);
		this._selectedOperationType = ko.observable(undefined);
		this._recordId = ko.observable(undefined);
		this._changedBy = ko.observable('');
		this._valueSearch = ko.observable('');

		this._tables = ko.observableArray([]);
		this._operations = ko.observableArray([]);

		this._sortColumn = ko.observable('ChangeDate');
		this._sortDirection = ko.observable('DESC');

		this._paginator = new Paginator();
		this._paginator.RecordsPerPage = 50;
		this._paginator.On(PAGINATOR_EVENTS.CHANGE, this, () => this.LoadData());
	}

	GetTemplateName(): string {
		return 'Pages/HistoryViewerPage/Templates/HistoryViewerPage';
	}

	Render(target: string): void {
		this._containerId = target;
		ko.cleanNode(document.getElementById(target));
		ko.applyBindings(this, document.getElementById(target));
	}

	private GetResultsEl(): HTMLElement | null {
		const container = this._containerId ? document.getElementById(this._containerId) : null;
		return container ? container.querySelector('.history-viewer-results') as HTMLElement : null;
	}

	AfterRender(el: Array<HTMLElement>): void {
		this._isReady(true);
		this.LoadFilterOptions();
		this.LoadData();
	}

	get Paginator(): Paginator {
		return this._paginator;
	}

	private LoadFilterOptions(): void {
		HistoryViewerStore.GetFilterOptions()
			.then(data => {
				this._tables(data.Tables);
				this._operations(data.Operations);
			})
			.fail(() => Notifier.Failed('Failed to load filter options'));
	}

	private LoadData(): void {
		// Supersede any in-flight request: register the new controller first, then
		// abort the previous one so its (now stale) handlers no-op via the guard below.
		const previous = this._activeRequest;
		const controller = new AbortController();
		this._activeRequest = controller;
		if (previous) {
			previous.abort();
		}

		this._isLoading(true);
		const resultsEl = this.GetResultsEl();
		if (resultsEl) {
			BlockUI.Block({ Target: resultsEl });
		} else {
			BlockUI.Block();
		}

		const unblock = () => {
			this._isLoading(false);
			if (resultsEl) {
				BlockUI.Unblock(resultsEl);
			} else {
				BlockUI.Unblock();
			}
		};

		const request: IHistoryViewerRequest = {
			Page: this._paginator.PageNumber,
			PageSize: this._paginator.RecordsPerPage,
			SortColumn: this._sortColumn(),
			SortDirection: this._sortDirection()
		};

		// CHANGEDATE is stored in UTC (history trigger uses GETUTCDATE), so send the picked
		// instant as UTC; the backend compares the date filter against the UTC column.
		if (this._dateFrom()) request.DateFrom = this._dateFrom() instanceof Date ? this._dateFrom().toISOString() : this._dateFrom();
		if (this._dateTo()) request.DateTo = this._dateTo() instanceof Date ? this._dateTo().toISOString() : this._dateTo();
		if (this._selectedTableId() != null) request.TableId = this._selectedTableId();
		if (this._selectedOperationType() != null) request.OperationType = this._selectedOperationType();
		const recordId = this._recordId();
		if (recordId != null && (recordId as any) !== '') request.RecordId = recordId;
		if (this._changedBy()) request.ChangedBy = this._changedBy();
		if (this._valueSearch()) request.ValueSearch = this._valueSearch();

		HistoryViewerStore.GetOperations(request, controller.signal)
			.then(result => {
				if (this._activeRequest !== controller) return;
				const items = result.Items.map(i => new HistoryViewerItemViewModel(i));
				this._items(items);

				this._paginator.SkipChangeTrigger = true;
				this._paginator.TotalRecords = result.TotalCount;
				this._paginator.PageNumber = result.Page;
				this._paginator.SkipChangeTrigger = false;

				this._paginator.UpdatePaginationInfo(result.Items);
				this._paginationInfo(this._paginator.PaginationRangeInfo());

				// Reflect server-side date clamping (>31 days) back to the picker
				if (result.EffectiveDateFrom) {
					const eff = new Date(result.EffectiveDateFrom);
					const cur = this._dateFrom() instanceof Date ? this._dateFrom() : (this._dateFrom() ? new Date(this._dateFrom()) : null);
					if (cur && !isNaN(eff.getTime()) && Math.abs(eff.getTime() - cur.getTime()) > 60000) {
						this._dateFrom(eff);
						Notifier.Warning(NOTIFICATIONS.HISTORY_DATE_RANGE_LIMITED);
					}
				}
			})
			.fail(err => {
				if (this._activeRequest !== controller) return;
				if (err && err.aborted) {
					Notifier.Warning(NOTIFICATIONS.HISTORY_REQUEST_CANCELLED);
				} else {
					Notifier.Failed(NOTIFICATIONS.FAILED_TO_LOAD_HISTORY);
				}
			})
			.always(() => {
				if (this._activeRequest !== controller) return;
				this._activeRequest = null;
				unblock();
			});
	}

	CancelRequest = (): void => {
		if (this._activeRequest) {
			this._activeRequest.abort();
		}
	};

	DateFromChanged = (value: any): void => {
		this._dateFrom(value || null);
	};

	DateToChanged = (value: any): void => {
		this._dateTo(value || null);
	};

	ApplyFilters = (): void => {
		var from = this._dateFrom();
		var to = this._dateTo();

		if (from && to) {
			var fromDate = from instanceof Date ? from : new Date(from);
			var toDate = to instanceof Date ? to : new Date(to);
			var diffDays = (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24);

			if (diffDays > 31) {
				Notifier.Warning('Date range cannot exceed 31 days');
				return;
			}
			if (diffDays < 0) {
				Notifier.Warning('Date From must be before Date To');
				return;
			}
		}

		this._paginator.SkipChangeTrigger = true;
		this._paginator.PageNumber = 1;
		this._paginator.SkipChangeTrigger = false;
		this.LoadData();
	};

	ClearFilters = (): void => {
		this._dateFrom(new Date());
		this._dateTo(new Date());
		this._selectedTableId(undefined);
		this._selectedOperationType(undefined);
		this._recordId(undefined);
		this._changedBy('');
		this._valueSearch('');
		this.ApplyFilters();
	};

	Sort = (column: string): void => {
		if (this._sortColumn() === column) {
			this._sortDirection(this._sortDirection() === 'ASC' ? 'DESC' : 'ASC');
		} else {
			this._sortColumn(column);
			this._sortDirection('DESC');
		}
		this.ApplyFilters();
	};

	SortIcon = (column: string): string => {
		if (this._sortColumn() !== column) return '';
		return this._sortDirection() === 'ASC' ? 'fa-sort-asc' : 'fa-sort-desc';
	};

	ToggleExpand = (item: HistoryViewerItemViewModel): void => {
		if (item.IsExpanded()) {
			item.IsExpanded(false);
			return;
		}

		item.IsLoadingDetails(true);
		item.IsExpanded(true);

		HistoryViewerStore.GetFieldChanges(item.OperationId, item.RecordId, item.TableId)
			.then(changes => item.FieldChanges(changes))
			.fail(() => Notifier.Failed('Failed to load field changes'))
			.always(() => item.IsLoadingDetails(false));
	};

	private _geoCache: { [ip: string]: string } = {};

	ShowLocation = (item: HistoryViewerItemViewModel): void => {
		if (!item.IpAddress || item.GeoLocation() === 'Loading...') return;

		if (this._geoCache[item.IpAddress]) {
			item.GeoLocation(this._geoCache[item.IpAddress]);
			return;
		}

		item.GeoLocation('Loading...');
		HistoryViewerStore.GetGeoLocation(item.IpAddress)
			.then(data => {
				const parts = [data.cityName, data.regionName, data.countryName].filter(Boolean);
				const location = parts.length > 0 ? parts.join(', ') : 'Unknown';
				this._geoCache[item.IpAddress] = location;
				item.GeoLocation(location);
			})
			.fail(() => item.GeoLocation(''));
	};
}
